import { packAcceptAttribution } from './pack-accept-bi';

/** The UTM tags on a query string, as `packAcceptAttribution` keeps them (a repeated tag is dropped). */
export function attributionOf(search: string): Record<string, string> {
  const params = new URLSearchParams(search);
  const query: Record<string, string | string[]> = {};
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key);
    query[key] = values.length === 1 ? values[0] : values;
  }
  return packAcceptAttribution(query);
}

/**
 * Where this visit came from, read ONCE, when the bundle first runs: a Wix link (the site-packs
 * email, the Business Manager page) tags Symphony's address with UTM, and pages like `/welcome`
 * replace their own URL within a second, so the address is only trustworthy at load. Module scope
 * rather than an effect because React runs a page's effects before the app's.
 *
 * It rides on EVERY page-view of the landing route, not just the first: one load fires several
 * (the sidebar's `menu`, a phone gate, the route itself) in an order that depends on the frame, so
 * "first one wins" would hand it to whichever happened to mount first. The first route change ends
 * the arrival; analysts pick the page they care about by `page_name`.
 */
let arrival: Record<string, string> =
  typeof window === 'undefined' ? {} : attributionOf(window.location.search);

if (typeof window !== 'undefined' && Object.keys(arrival).length > 0) {
  // Dynamic so this module stays importable where the router is not (tests, server).
  void import('next/router').then(({ default: Router }) => {
    const end = (): void => {
      arrival = {};
      Router.events.off('routeChangeComplete', end);
    };
    Router.events.on('routeChangeComplete', end);
  });
}

/** The arrival's tags while the visit is still on the route it landed on; empty after that. */
export function currentArrival(): Record<string, string> {
  return arrival;
}

/** Tests only: set what `currentArrival` answers. */
export function setArrivalForTest(search: string): void {
  arrival = attributionOf(search);
}
